import { NgModule } from '@angular/core';
import { Route, RouterModule, Routes } from '@angular/router';
import { getRoleContext, userRoleContext } from './constants/role-details.constants';
import { UserProfileComponent } from './modules/user-profile/user-profile.component';
import { MainTabsComponent } from './blocks/main-tabs/main-tabs.component';
import { authGuard } from './shared/auth.gaurd';
import { tcGuard } from './shared/tc.guard';

export function getRouteDetails() {
  let roleContext = getRoleContext();
  let routes: Routes = [];

  const tcRoute = {
    path: 'terms-and-condition',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./modules/terms-and-condition/terms-and-condition.component').then(
        m => m.TermsAndConditionComponent
      )
  };

  // Fall-through for ANY path not in the current role's route table:
  // ALL unknown paths (including invalid tenant, base URL, wrong role routes) 
  // redirect to /terms-and-condition which has authGuard.
  // 
  // authGuard handles:
  //  - Invalid/missing tenant → redirect to MAG login portal
  //  - Valid tenant but not authenticated → redirect to backend for WS-Fed login
  //  - Authenticated → show terms-and-condition page
  //
  // This ensures users MUST authenticate before seeing any content.
  const childWildcard: Route = {
    path: '**',
    redirectTo: '/terms-and-condition'
  };

  // System-level boundary pages — must remain reachable for every role
  // without an auth guard, otherwise the guard redirect would loop back
  // onto these pages. Note: getRoleContext() always returns a role (defaults
  // to Buyer when unset).
  const unauthorizedRoute = {
    path: 'unauthorized',
    loadComponent: () =>
      import('./modules/unauthorized-access/unauthorized-access.component').then(
        m => m.UnauthorizedAccessComponent
      )
  };

  const serverErrorRoute = {
    path: 'server-error',
    loadComponent: () =>
      import('./modules/server-error/server-error.component').then(
        m => m.ServerErrorComponent
      )
  };

  if (roleContext) {
    switch (roleContext) {
      case userRoleContext.Buyer:
        routes = [
          tcRoute,
          unauthorizedRoute,
          serverErrorRoute,
          {
            path: '',
            component: MainTabsComponent,
            children: [
              {
                canActivate: [authGuard, tcGuard],
                path: 'home', loadChildren: () => import('./modules/home/home.module').then(m => m.HomeModule), data: {
                  breadcrumb: { alias: 'Home' }
                }
              },
              {
                canActivate: [authGuard, tcGuard],
                path: 'dashboard', loadComponent: () => import('./modules/dashboard/dashboard.component').then(m => m.DashboardComponent),
                children: [
                  {
                    path: 'dashboard-details', loadComponent: () => import('./modules/dashboard/dashboard-details/dashboard-details.component').then(m => m.DashboardDetailsComponent), data: {
                      breadcrumb: { alias: 'Dashboard' }
                    }
                  },
                  {
                    path: 'supplier-search', loadComponent: () => import('./modules/dashboard/suppliers-list/suppliers-list.component').then(m => m.SuppliersListComponent), data: {
                      breadcrumb: { alias: 'Supplier Search' }
                    }
                  },
                  {
                    path: 'search-score', loadComponent: () => import('./modules/dashboard/search-score/search-score.component').then(m => m.SearchScoreComponent)
                  },
                  { path: '**', redirectTo: 'dashboard-details', pathMatch: 'full' }
                ]
              },
              {
                path: 'search-page', pathMatch: 'prefix', loadChildren: () => import('./modules/search-page/search-page.module').then(m => m.SearchPageModule), data: {
                  breadcrumb: { alias: 'Search Suppliers' }
                },
                canActivate: [authGuard, tcGuard]
              },
              {
                path: 'groups', loadChildren: () => import('./modules/groups/groups.module').then(m => m.GroupsModule), data: {
                  breadcrumb: { alias: 'Groups' }
                },
                canActivate: [authGuard, tcGuard]
              },
              {
                path: 'administration', loadChildren: () => import('./modules/administration/administration.module').then(m => m.AdministrationModule)
              },
              {
                path: 'campaign', loadChildren: () => import('./modules/campaigns/campaigns.module').then(m => m.CampaignsModule), data: {
                  breadcrumb: { alias: 'Campaign' }
                },
                canActivate: [authGuard, tcGuard]
              },
              {
                path: 'enrollment/:type', loadChildren: () => import('./modules/enrollment/enrollment.module').then(m => m.EnrollmentModule), data: {
                  breadcrumb: { alias: 'Enrollment' }
                },
                canActivate: [authGuard, tcGuard]
              },
              {
                path: 'administration', loadChildren: () => import('./modules/administration/administration.module').then(m => m.AdministrationModule),
                canActivate: [authGuard, tcGuard]
              },
              {
                path: 'form-management', loadChildren: () => import('./modules/form-management/form-management.module').then(m => m.FormManagementModule),
                canActivate: [authGuard, tcGuard]
              },
              {
                path: 'organization', loadChildren: () => import('./modules/organization/organization.module').then(m => m.OrganizationModule),
                canActivate: [authGuard, tcGuard]
              },
              {
                path: 'user-profile/:id',
                component: UserProfileComponent,
                canActivate: [authGuard, tcGuard]
              },
              {
                path: 'reports',
                loadComponent: () => import('./modules/reporting-tool/reporting-tool.component').then(m => m.ReportingToolComponent),
                data: { breadcrumb: { alias: 'Reports' } },
                canActivate: [authGuard, tcGuard]
              },
              { path: '', redirectTo: 'home', pathMatch: 'full' },
              childWildcard
            ]
          }
        ];
        break;
      case userRoleContext.Support:
        routes = [
          tcRoute,
          unauthorizedRoute,
          serverErrorRoute,
          {
            path: '',
            component: MainTabsComponent,
            children: [
              {
                path: 'home', loadChildren: () => import('./modules/support-admin/support-home/support-home.module').then(m => m.SupportHomeModule), data: {
                  breadcrumb: { alias: 'Home' }
                },
                canActivate: [authGuard, tcGuard]
              },
              {
                canActivate: [authGuard, tcGuard],
                path: 'campaign', loadChildren: () => import('./modules/support-admin/support-campaign/support-campaign.module').then(m => m.SupportCampaignModule), data: {
                  breadcrumb: { alias: 'Campaign(s)' }
                }
              },
              {
                canActivate: [authGuard, tcGuard],
                path: 'form-management', loadChildren: () => import('./modules/support-admin/support-form-management/support-form-management.module').then(m => m.SupportFormManagementModule), data: {
                  breadcrumb: { alias: 'Form Management' }
                }
              },
              {
                path: 'formset-list', loadChildren: () => import('./modules/support-admin/support-fortset-list/support-formset-list.module').then(m => m.SupportFormsetListModule), data: {
                  breadcrumb: { alias: 'FormSet Management' }
                }
              },
              { path: '', redirectTo: 'home', pathMatch: 'full' },
              childWildcard
            ]
          }
        ];
        break;
      case userRoleContext.Supplier:
        routes = [
          tcRoute,
          unauthorizedRoute,
          serverErrorRoute,
          {
            path: '',
            component: MainTabsComponent,
            children: [
              {
                canActivate: [authGuard, tcGuard],
                path: 'supplier-home', loadChildren: () => import('./modules/supplier-view/supplier-home/supplier-home.module').then(m => m.SupplierHomeModule), data: {
                  breadcrumb: { alias: 'Home' }
                }
              },
              {
                canActivate: [authGuard, tcGuard],
                path: 'form-management', loadChildren: () => import('./modules/supplier-view/supplier-form-management/supplier-form-management.module').then(m => m.SupplierFormManagementModule), data: {
                  breadcrumb: { alias: 'Form Management' }
                }
              },
              {
                canActivate: [authGuard, tcGuard],
                path: 'my-organization', loadChildren: () => import('./modules/supplier-view/supplier-my-organization/supplier-my-organization.module').then(m => m.SupplierMyOrganizationModule), data: {
                  breadcrumb: { alias: 'My Organization' }
                }
              },
              {
                canActivate: [authGuard, tcGuard],
                path: 'my-links', loadChildren: () => import('./modules/supplier-view/supplier-my-links/supplier-my-links.module').then(m => m.SupplierMyLinksModule), data: {
                  breadcrumb: { alias: 'My Links' }
                }
              },
              {
                path: 'user-profile/:id',
                component: UserProfileComponent,
                canActivate: [authGuard, tcGuard]
              },
              {
                canActivate: [authGuard, tcGuard],
                path: 'communications', loadChildren: () => import('./modules/supplier-view/supplier-communications/supplier-communications.module').then(m => m.SupplierCommunicationsModule), data: {
                  breadcrumb: { alias: 'Communications' }
                }
              },
              { path: '', redirectTo: 'supplier-home', pathMatch: 'full' },
              childWildcard
            ]
          }
        ];
        break;
      default:
        routes = [tcRoute, unauthorizedRoute, serverErrorRoute];
    }
  }
  return routes;
}

@NgModule({
  imports: [RouterModule.forRoot(getRouteDetails())],
  exports: [RouterModule]
})
export class AppRoutingModule { }
